/**
 * Landing Page
 * Public-facing page for prospective charter operators
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EnvelopeIcon,
  PaperAirplaneIcon,
  ClipboardDocumentCheckIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  WrenchScrewdriverIcon,
  ChartBarIcon,
  CheckIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';

const CONTACT_EMAIL = 'team@frostypilots.com';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function FeatureCard({ icon, title, description }: FeatureCardProps) {
  return (
    <div
      className="p-6 rounded-xl border transition-all duration-200 hover:shadow-lg"
      style={{
        backgroundColor: 'var(--bg-primary)',
        borderColor: 'var(--border-subtle)',
      }}
    >
      <div
        className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
        style={{ backgroundColor: 'var(--color-info-bg)', color: 'var(--color-primary-500)' }}
      >
        {icon}
      </div>
      <h3
        className="text-lg font-semibold mb-2"
        style={{ color: 'var(--text-primary)' }}
      >
        {title}
      </h3>
      <p
        className="text-sm leading-relaxed"
        style={{ color: 'var(--text-secondary)' }}
      >
        {description}
      </p>
    </div>
  );
}

interface StepProps {
  number: number;
  title: string;
  description: string;
}

function Step({ number, title, description }: StepProps) {
  return (
    <div className="flex gap-4">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-bold text-lg"
        style={{
          backgroundColor: 'var(--color-primary-500)',
          color: 'var(--text-inverse)',
        }}
      >
        {number}
      </div>
      <div>
        <h3
          className="font-semibold text-base mb-1"
          style={{ color: 'var(--text-primary)' }}
        >
          {title}
        </h3>
        <p
          className="text-sm"
          style={{ color: 'var(--text-secondary)' }}
        >
          {description}
        </p>
      </div>
    </div>
  );
}

const FEATURES: FeatureCardProps[] = [
  {
    icon: <EnvelopeIcon className="w-6 h-6" />,
    title: 'Email-to-Trip Automation',
    description:
      'Forward your crew briefing email and the system creates the trip, assigns crew, and parses flight legs automatically. No manual data entry.',
  },
  {
    icon: <DevicePhoneMobileIcon className="w-6 h-6" />,
    title: 'Pilot Mobile Workflow',
    description:
      'Pilots see their upcoming trips, complete flight details leg-by-leg on their phone, and submit for approval. PIN login for quick ramp access.',
  },
  {
    icon: <CurrencyDollarIcon className="w-6 h-6" />,
    title: 'Pay Calculation & Tracking',
    description:
      'Per-pilot pay rates with overtime at 30hrs, short-notice premiums, and historical accuracy. Every rate change audited for payroll compliance.',
  },
  {
    icon: <ClipboardDocumentCheckIcon className="w-6 h-6" />,
    title: 'Flight Approval Queue',
    description:
      'Admin reviews submitted flights, approves for payment, tracks the full lifecycle from pending through paid. No spreadsheets.',
  },
  {
    icon: <WrenchScrewdriverIcon className="w-6 h-6" />,
    title: 'MEL Management',
    description:
      'Upload your MMEL, search by ATA chapter, track active deferrals with expiry alerts. Know what\'s deferred before you dispatch.',
  },
  {
    icon: <ShieldCheckIcon className="w-6 h-6" />,
    title: 'Safety & Compliance',
    description:
      'Built-in SMS safety reporting per TC CARs 107. Non-punitive reporting integrated into the flight workflow. Qualification and medical expiry tracking.',
  },
  {
    icon: <ChartBarIcon className="w-6 h-6" />,
    title: 'Operations Dashboard',
    description:
      'KPIs at a glance — flights this month, pending approvals, fleet status, compliance alerts. Built for the chief pilot\'s morning briefing.',
  },
  {
    icon: <PaperAirplaneIcon className="w-6 h-6" />,
    title: 'Digital Logbook',
    description:
      'Every approved flight automatically feeds the pilot logbook. Multi-crew, multi-type, exportable. No more end-of-month log reconciliation.',
  },
];

function scrollToContact() {
  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
}

interface ContactFormData {
  name: string;
  email: string;
  operator: string;
  fleetSize: string;
  message: string;
}

const INITIAL_FORM: ContactFormData = {
  name: '',
  email: '',
  operator: '',
  fleetSize: '',
  message: '',
};

function ContactForm() {
  const [form, setForm] = useState<ContactFormData>(INITIAL_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const isValid = form.name.trim() && form.email.trim() && form.email.includes('@');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isValid) {
      setError('Please fill in your name and a valid email.');
      return;
    }

    const subject = encodeURIComponent('FrostyPilots Demo Request');
    const bodyLines = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      form.operator ? `Operator: ${form.operator}` : '',
      form.fleetSize ? `Fleet size: ${form.fleetSize}` : '',
      '',
      form.message || 'I\'d like to learn more about FrostyPilots.',
    ].filter(Boolean);
    const body = encodeURIComponent(bodyLines.join('\n'));

    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  const updateField = (field: keyof ContactFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ backgroundColor: 'var(--color-success-bg)', color: 'var(--color-success)' }}
        >
          <CheckCircleIcon className="w-8 h-8" />
        </div>
        <h3
          className="text-xl font-semibold mb-2"
          style={{ color: 'var(--text-primary)' }}
        >
          Thanks for reaching out
        </h3>
        <p
          className="text-sm mb-4"
          style={{ color: 'var(--text-secondary)' }}
        >
          Your email client should have opened with a pre-filled message.
          If it didn't, email us directly at{' '}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="underline"
            style={{ color: 'var(--color-primary-500)' }}
          >
            {CONTACT_EMAIL}
          </a>
        </p>
        <Button variant="ghost" size="sm" onClick={() => setSubmitted(false)}>
          Send another message
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Input
          label="Name"
          placeholder="Your name"
          value={form.name}
          onChange={(e) => updateField('name', e.target.value)}
          required
        />
        <Input
          label="Email"
          type="email"
          placeholder="you@operator.com"
          value={form.email}
          onChange={(e) => updateField('email', e.target.value)}
          required
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Input
          label="Operator / Company"
          placeholder="Your company name"
          value={form.operator}
          onChange={(e) => updateField('operator', e.target.value)}
        />
        <Input
          label="Fleet size"
          placeholder="e.g. 3 aircraft, 8 pilots"
          value={form.fleetSize}
          onChange={(e) => updateField('fleetSize', e.target.value)}
        />
      </div>
      <div>
        <label
          className="block text-sm font-medium mb-1.5 text-foreground"
          htmlFor="contact-message"
        >
          Message
        </label>
        <textarea
          id="contact-message"
          rows={4}
          placeholder="Tell us about your operation, or just say hello"
          value={form.message}
          onChange={(e) => updateField('message', e.target.value)}
          className="w-full px-3 py-2 rounded-md border bg-background text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-3 focus:ring-primary/50 transition-colors duration-200 border-border resize-none text-sm"
        />
      </div>

      {error && (
        <p className="text-sm text-danger" role="alert">{error}</p>
      )}

      <Button type="submit" size="lg" fullWidth disabled={!isValid}>
        Send Message
      </Button>
      <p
        className="text-xs text-center"
        style={{ color: 'var(--text-tertiary)' }}
      >
        Opens your email client with a pre-filled message to {CONTACT_EMAIL}
      </p>
    </form>
  );
}

const CHECKLIST = [
  'Runs 2-15 pilots on charter or medevac',
  'Operations sends crew briefings via email',
  'Tired of spreadsheets for pay and flight tracking',
  'Needs TC CARs compliance documentation',
  'Wants pilots on a mobile-first workflow',
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      {/* Nav Bar */}
      <nav
        className="border-b sticky top-0 z-50 backdrop-blur-sm"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--bg-primary) 85%, transparent)',
          borderColor: 'var(--border-subtle)',
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <span
            className="text-xl font-bold tracking-tight"
            style={{ color: 'var(--text-primary)' }}
          >
            FrostyPilots
          </span>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/login')}
            >
              Sign In
            </Button>
            <Button size="sm" onClick={scrollToContact}>
              Request Demo
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1
            className="text-4xl sm:text-5xl font-bold tracking-tight mb-6 leading-tight"
            style={{ color: 'var(--text-primary)' }}
          >
            Crew management built{' '}
            <span style={{ color: 'var(--color-primary-500)' }}>
              for small charter ops
            </span>
          </h1>
          <p
            className="text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
            style={{ color: 'var(--text-secondary)' }}
          >
            Forward your crew briefing. Trips get created, pilots get notified,
            flights get tracked, pay gets calculated. That simple.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={scrollToContact}>
              Request a Demo
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => {
                document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              See How It Works
            </Button>
          </div>
        </div>
      </section>

      {/* Social proof / positioning strip */}
      <section
        className="py-8 border-y"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--border-subtle)',
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <p
            className="text-center text-sm font-medium"
            style={{ color: 'var(--text-tertiary)' }}
          >
            Designed by a charter pilot, for charter operators.
            Built around Canadian aviation regulations (TC CARs).
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 px-4 sm:px-6" id="features">
        <div className="max-w-4xl mx-auto">
          <h2
            className="text-2xl sm:text-3xl font-bold text-center mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            From briefing email to paid flight in three steps
          </h2>
          <p
            className="text-center mb-12 max-w-xl mx-auto"
            style={{ color: 'var(--text-secondary)' }}
          >
            No training manual required. If your ops coordinator can send an email, you're ready.
          </p>

          <div className="grid gap-8 max-w-lg mx-auto">
            <Step
              number={1}
              title="Ops forwards the crew briefing"
              description="The system reads the email, extracts the Q-number, crew assignments, aircraft, and flight legs. Trip is created and pilots are notified."
            />
            <Step
              number={2}
              title="Pilots complete flights on their phone"
              description="Each crew member sees their assigned trips, fills in times and details leg-by-leg via a card-based wizard. PIN login works great on the ramp."
            />
            <Step
              number={3}
              title="Admin approves and pay is calculated"
              description="Review submitted flights in the approval queue. One tap to approve. Pay is calculated automatically with the correct rates, overtime, and premiums."
            />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section
        className="py-16 sm:py-20 px-4 sm:px-6"
        style={{ backgroundColor: 'var(--bg-secondary)' }}
      >
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-2xl sm:text-3xl font-bold text-center mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            Everything a small operator actually needs
          </h2>
          <p
            className="text-center mb-12 max-w-xl mx-auto"
            style={{ color: 'var(--text-secondary)' }}
          >
            No bloat. No modules you'll never use. Just the tools that keep a
            2-15 pilot operation running smoothly.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((feature) => (
              <FeatureCard key={feature.title} {...feature} />
            ))}
          </div>
        </div>
      </section>

      {/* Is this for you? */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2
                className="text-2xl sm:text-3xl font-bold mb-4"
                style={{ color: 'var(--text-primary)' }}
              >
                Built for operators like you
              </h2>
              <p
                className="mb-6"
                style={{ color: 'var(--text-secondary)' }}
              >
                FrostyPilots is purpose-built for small Canadian charter and medevac
                operators. If this sounds like your operation, we should talk.
              </p>
              <Button size="lg" onClick={scrollToContact}>
                Request a Demo
              </Button>
            </div>
            <div className="space-y-4">
              {CHECKLIST.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                    style={{
                      backgroundColor: 'var(--color-success-bg)',
                      color: 'var(--color-success)',
                    }}
                  >
                    <CheckIcon className="w-4 h-4" />
                  </div>
                  <span
                    className="text-sm"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section
        id="contact"
        className="py-16 sm:py-20 px-4 sm:px-6 border-t"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--border-subtle)',
        }}
      >
        <div className="max-w-xl mx-auto">
          <h2
            className="text-2xl sm:text-3xl font-bold text-center mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            Get in touch
          </h2>
          <p
            className="text-center mb-8"
            style={{ color: 'var(--text-secondary)' }}
          >
            Tell us about your operation and we'll set up a 30-minute walkthrough
            tailored to your fleet and crew size.
          </p>
          <div
            className="rounded-xl border p-6 sm:p-8"
            style={{
              backgroundColor: 'var(--bg-primary)',
              borderColor: 'var(--border-subtle)',
            }}
          >
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="py-8 px-4 sm:px-6 border-t"
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderColor: 'var(--border-subtle)',
        }}
      >
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <span
            className="text-sm font-medium"
            style={{ color: 'var(--text-tertiary)' }}
          >
            FrostyPilots
          </span>
          <span
            className="text-xs"
            style={{ color: 'var(--text-tertiary)' }}
          >
            Charter crew management for Canadian operators
          </span>
        </div>
      </footer>
    </div>
  );
}
